import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { TES_STAGES } from "@/components/NextRace";
import { SectionMark } from "@/components/PageHeader";

const TES_URL = "https://www.toscanoenduroseries.it";
const TES_RACE_URL = "https://www.toscanoenduroseries.it/iscrizioni/274-tes-6-porto-santo-stefano-gr-4-ottobre-2026-camp-toscano-fci";
export const TES_MAP_IMG = "https://www.toscanoenduroseries.it/images/2026/6_PortoSStefano/TES6_2026_girogara.jpg";
const TES_LINKS = [
    { key: "mappa", it: "Mappa giro gara", en: "Course map", href: TES_MAP_IMG },
    { key: "altimetria", it: "Altimetria", en: "Elevation profile", href: "https://www.toscanoenduroseries.it/images/2026/6_PortoSStefano/TES6_2026_altimetria.jpg" },
    { key: "gpx", it: "Traccia GPX", en: "GPX track", href: "https://drive.google.com/file/d/1qlKjVKeJf9DbObvJzHitDei3PaGP7ptC/view?usp=sharing" },
    { key: "scheda", it: "Scheda gara ufficiale", en: "Official race page", href: TES_RACE_URL },
    { key: "sito", it: "toscanoenduroseries.it", en: "toscanoenduroseries.it", href: TES_URL },
];
/* Official weekend schedule as it was published — kept as historical record */
const TES_PROGRAM = [
    { day: "sab", date: "03.10", items: [
        { time: "09:00 – 18:30", it: "Allestimento area Paddock", en: "Paddock set-up" },
        { time: "15:00 – 18:30", it: "Consegna pacco gara e controllo tessere", en: "Race pack pick-up & licence check" },
    ]},
    { day: "dom", date: "04.10", items: [
        { time: "07:00 – 07:30", it: "Verifica licenze e ritiro tabelle / pacchi gara", en: "Licence check & number plates / race packs" },
        { time: "07:45", it: "Briefing tecnico atleti", en: "Riders' technical briefing" },
        { time: "08:00", it: "Partenza primo concorrente", en: "First rider starts", hot: true },
        { time: "15:00", it: "Arrivo ultimo atleta", en: "Last rider finishes" },
        { time: "16:00", it: "Premiazioni", en: "Podium ceremony", hot: true },
    ]},
];
const TES_CATS = ["Muscolare Maschile", "Muscolare Femminile", "E-Bike Maschile", "E-Bike Femminile"];

/* TES 2026 archive: course, special stages (our trails), official links, weekend schedule. No live/pre-race CTAs. */
export default function TesArchiveBlock({ lang, onOpenImage, n = "02" }) {
    const en = lang === "en";
    return (
        <div data-testid="tes-archive" className="space-y-10">
            <div>
                <SectionMark n={n} title={en ? "The race course" : "Il giro gara"}
                    right={<span className="label-mono text-abnormal-paperDim/60">37 km · 1254 m · 4 PS</span>} />
                <div className="grid md:grid-cols-12 gap-6 items-start">
                    <button type="button" onClick={() => onOpenImage(TES_MAP_IMG)} data-testid="tes-map-thumb"
                        className="md:col-span-5 group relative block w-full border border-white/15 hover:border-abnormal-acid bg-abnormal-charcoal overflow-hidden transition-colors">
                        <img src={TES_MAP_IMG} alt={en ? "TES 2026 course map" : "Mappa giro gara TES 2026"} loading="lazy" className="w-full h-auto block" />
                        <span className="absolute bottom-3 left-3 label-mono bg-black/85 text-abnormal-paper border border-abnormal-paper/30 px-2 py-1">{en ? "OFFICIAL COURSE MAP" : "MAPPA UFFICIALE"}</span>
                    </button>
                    <div className="md:col-span-7">
                        <p className="label-mono text-abnormal-acid">{en ? "Special stages · our trails" : "Prove speciali · i nostri trail"}</p>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                            {Object.entries(TES_STAGES).sort((a, b) => a[1].ps - b[1].ps).map(([slug, st]) => (
                                <Link key={slug} to={`/trail/${slug}`} data-testid={`tes-stage-${slug}`}
                                    className="group border border-white/15 hover:border-abnormal-magenta bg-abnormal-charcoal px-3 py-2.5 transition-colors">
                                    <span className="block font-vt text-[10px] tracking-[0.25em] text-abnormal-magenta">PS{st.ps} · {st.km} km</span>
                                    <span className="block font-display uppercase text-xl leading-none mt-1 text-abnormal-paper group-hover:text-abnormal-magenta capitalize">{slug}<span className="text-abnormal-acid">.</span></span>
                                    <span className="block mt-1 label-mono text-abnormal-paperDim/70">{en ? "Trail page" : "Scheda trail"} →</span>
                                </Link>
                            ))}
                        </div>
                        <div data-testid="tes-prizes" className="mt-5 border border-white/15 bg-abnormal-charcoal p-4">
                            <p className="label-mono text-abnormal-magenta">{en ? "ABNORMAL prizes · 4 categories" : "Premi ABNORMAL · 4 categorie"}</p>
                            <ul className="mt-3 grid grid-cols-2 gap-2">
                                {TES_CATS.map((c, i) => (
                                    <li key={c} className="border border-white/10 px-3 py-2 flex items-baseline gap-2">
                                        <span className="font-vt text-[10px] text-abnormal-acid">0{i + 1}</span>
                                        <span className="font-display uppercase text-base md:text-lg leading-none text-abnormal-paper">{c}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div data-testid="tes-useful-links" className="mt-5">
                            <p className="label-mono text-abnormal-acid">{en ? "Official documents" : "Documenti ufficiali"}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {TES_LINKS.map((l) => (
                                    <a key={l.key} href={l.href} target="_blank" rel="noreferrer" data-testid={`tes-link-${l.key}`}
                                        className="inline-flex items-center gap-1.5 border border-white/15 hover:border-abnormal-acid hover:text-abnormal-acid text-abnormal-paperDim px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-bold transition-colors">
                                        {en ? l.en : l.it} <ArrowUpRight size={11} />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div data-testid="tes-program">
                <SectionMark n={String(Number(n) + 1).padStart(2, "0")} title={en ? "Race weekend · how it went" : "Weekend di gara · com'è andato"}
                    right={<span className="label-mono text-abnormal-paperDim/60 hidden sm:inline">Lungomare Navigatori · Porto S. Stefano</span>} />
                <div className="grid md:grid-cols-2 gap-4">
                    {TES_PROGRAM.map((d) => (
                        <div key={d.day} data-testid={`tes-program-${d.day}`} className="border border-white/15 bg-abnormal-charcoal p-4">
                            <p className="font-display uppercase text-2xl leading-none text-abnormal-paper">
                                {d.day === "sab" ? (en ? "Saturday" : "Sabato") : (en ? "Sunday" : "Domenica")} <span className="text-abnormal-acid">{d.date}</span>
                            </p>
                            <ol className="mt-3 relative border-l border-white/15 ml-1.5 space-y-3">
                                {d.items.map((it) => (
                                    <li key={it.time} className="pl-4 relative">
                                        <span className={`absolute -left-[5px] top-1.5 w-2 h-2 rounded-full ${it.hot ? "bg-abnormal-acid" : "bg-abnormal-paperDim/60"}`} />
                                        <p className="font-vt text-[11px] tracking-[0.2em] text-abnormal-acid">{it.time}</p>
                                        <p className={`text-sm leading-snug ${it.hot ? "text-abnormal-paper font-bold" : "text-abnormal-paperDim"}`}>{en ? it.en : it.it}</p>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
